import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Lock, Megaphone, Inbox } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import type { ReportListItem, ReportStatus, ReportType } from '../../../types/whs';
import {
  StatusBadge, SeverityBadge, Badge,
  REPORT_TYPE_LABEL, REPORT_STATUS_LABEL,
} from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatDateTimeInTz } from '../../../utils/timezone';

const REPORT_STATUSES = Object.keys(REPORT_STATUS_LABEL) as ReportStatus[];
const REPORT_TYPES = Object.keys(REPORT_TYPE_LABEL) as ReportType[];

const selectCls =
  'rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none';

function assigneeName(r: ReportListItem): string {
  const n = [r.assignee_first_name, r.assignee_last_name].filter(Boolean).join(' ').trim();
  return n || 'Unassigned';
}

function WhsReportsInner() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [searchParams] = useSearchParams();

  const initialStatus = (searchParams.get('status') as ReportStatus | null) ?? '';
  const [statusFilter, setStatusFilter] = useState<ReportStatus | ''>(initialStatus);
  const [typeFilter, setTypeFilter] = useState<ReportType | ''>('');
  const [notifiableOnly, setNotifiableOnly] = useState(false);

  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await whsService.listReports({
      status: statusFilter || undefined,
      report_type: typeFilter || undefined,
      is_notifiable: notifiableOnly ? true : undefined,
    });
    if (res.success) setReports(res.data);
    else setError(res.error.message);
    setLoading(false);
  }, [statusFilter, typeFilter, notifiableOnly]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Incidents & Hazards"
        subtitle="Reported incidents, near-misses, hazards & illnesses"
        icon={<AlertTriangle className="h-6 w-6 text-white" />}
        actionButtons={
          <Button size="sm" onClick={() => navigate('/whs/report')}>
            <Megaphone className="h-4 w-4" /> Report a safety issue
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <select
            className={selectCls}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ReportStatus | '')}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {REPORT_STATUSES.map((s) => (
              <option key={s} value={s}>{REPORT_STATUS_LABEL[s]}</option>
            ))}
          </select>
          <select
            className={selectCls}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as ReportType | '')}
            aria-label="Filter by type"
          >
            <option value="">All types</option>
            {REPORT_TYPES.map((t) => (
              <option key={t} value={t}>{REPORT_TYPE_LABEL[t]}</option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              checked={notifiableOnly}
              onChange={(e) => setNotifiableOnly(e.target.checked)}
            />
            Notifiable only
          </label>
        </div>

        {loading ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : reports.length === 0 ? (
          <div className="rounded-xl border border-neutral-200 bg-white p-10 text-center">
            <Inbox className="mx-auto h-10 w-10 text-neutral-300" />
            <p className="mt-3 text-sm font-medium text-neutral-700">No reports match your filters</p>
            <p className="mt-1 text-sm text-neutral-500">Reports submitted by workers will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/hr/whs/reports/${r.id}`)}
                className="w-full text-left rounded-xl border border-neutral-200 bg-white p-5 hover:border-primary-300 hover:shadow-sm transition-all"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-neutral-400">{r.tracking_code}</span>
                      <Badge label={REPORT_TYPE_LABEL[r.report_type]} />
                      {r.is_notifiable && (
                        <Badge label="Notifiable" className="bg-red-100 text-red-800" />
                      )}
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-neutral-900 truncate">{r.title}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500">
                      <span className="inline-flex items-center gap-1">
                        {r.is_anonymous && <Lock className="h-3 w-3 text-neutral-400" />}
                        {r.is_anonymous ? 'Anonymous' : r.reporter_display}
                      </span>
                      {(r.farm_name || r.location) && (
                        <span>{[r.farm_name, r.location].filter(Boolean).join(' · ')}</span>
                      )}
                      <span>{formatDateTimeInTz(r.reported_at, orgTz)}</span>
                      <span>Assigned: {assigneeName(r)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <SeverityBadge severity={r.severity} />
                    <StatusBadge status={r.status} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WhsReports() {
  return <WhsGate><WhsReportsInner /></WhsGate>;
}
